import React, {} from 'react';
import { CallActivityMetrics } from '../../../store/actions/calls/payloads';
import { convertFormatFromBackendToDayJs } from '../../../utils/dateWithTimezoneConversion';
import {TooltipProps} from 'recharts';
import {getDurationFromSec} from '../../../utils/transformers';
import moment from 'moment';
import {
    CallStatisticsMetricsItem,
    extractDateOnly,
    getFullDayDifference,
    extractTimeOnly,
    useStyles
} from './CallStatistics.utils';
import {useTranslation} from 'react-i18next';

export type CallStatisticsTooltipProps = {
    props: TooltipProps<string, string>,
    call_activity_metrics_payload: CallActivityMetrics | undefined,
    userDateTimeFormat: string, 
    call_activity_metrics_payload_to_compare: CallActivityMetrics | undefined,
    data: CallStatisticsMetricsItem[],
}

export const CallStatisticsTooltip: React.VFC<CallStatisticsTooltipProps> = ({
    props,
    call_activity_metrics_payload, 
    userDateTimeFormat, 
    call_activity_metrics_payload_to_compare,
    data
}) => 
    {
        const defaultDateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
        const classes = useStyles();
        const {t} = useTranslation();

        const dtf = convertFormatFromBackendToDayJs(userDateTimeFormat);
        const isDrawCompareWith = (props?.payload?.length ?? 0) > 2;
        const incomingValue = +(props?.payload?.find(e => e.name === 'incoming')?.value ?? '0');
        const outgoingValue = +(props?.payload?.find(e => e.name === 'outgoing')?.value ?? '0');
        
        const incomingCompareValue = +(props?.payload?.find(e => e.name === 'incoming_compare')?.value ?? '0');
        const outgoingCompareValue = +(props?.payload?.find(e => e.name === 'outgoing_compare')?.value ?? '0');

        //"week" | "day" | "hour" | "min";
        const unit = call_activity_metrics_payload?.metric_list?.[0].aggregator_list?.[0].unit;

        const currentDataItemIndex = data.findIndex(e => e === props.payload?.[0].payload);
        let currentDataItem = null;
        let nextDataItem = null;
        if(currentDataItemIndex != -1) {
            currentDataItem = data[currentDataItemIndex];
            if(currentDataItemIndex != data.length - 1){
                nextDataItem = data[currentDataItemIndex + 1]
            }
        }

        let dateLeft = props.label;
        let dateRight = props.label;

        const nextPeriodEndTime = nextDataItem?.date
            ? moment(nextDataItem.date).subtract(1, 'second').format(defaultDateTimeFormat)
            : moment(call_activity_metrics_payload?.date_to ?? new Date()).subtract(1, 'second').format(defaultDateTimeFormat);

        const nextPeriodEndTimeCompare = nextDataItem?.date_compare
            ? moment(nextDataItem.date_compare).subtract(1, 'second').format(defaultDateTimeFormat)
            : moment(call_activity_metrics_payload_to_compare?.date_to ?? new Date()).subtract(1, 'second').format(defaultDateTimeFormat);

        let widerDateFormat = false;
        switch(unit)
        {
            case 'week':
                const d1 = extractDateOnly(moment(currentDataItem?.date).format(defaultDateTimeFormat), defaultDateTimeFormat, dtf);
                const d2 = extractDateOnly(nextPeriodEndTime, defaultDateTimeFormat, dtf);
                dateLeft = d1 + ' — ' + d2;
                const d1r = extractDateOnly(moment(currentDataItem?.date_compare).format(defaultDateTimeFormat), defaultDateTimeFormat, dtf);
                const d2r = extractDateOnly(nextPeriodEndTimeCompare, defaultDateTimeFormat, dtf);
                dateRight = d1r + ' — ' + d2r;
                break;
            case 'day':
                dateLeft = extractDateOnly(moment(currentDataItem?.date).format(defaultDateTimeFormat), defaultDateTimeFormat, dtf);
                dateRight = extractDateOnly(moment(currentDataItem?.date_compare).format(defaultDateTimeFormat), defaultDateTimeFormat, dtf);
                break;
            case 'hour':
            case 'min':
                const fullDiffDays = getFullDayDifference(call_activity_metrics_payload?.date_to ?? '', 
                    call_activity_metrics_payload?.date_to ?? '', defaultDateTimeFormat);
                const d3 = fullDiffDays >= 1
                    ? extractTimeOnly(moment(currentDataItem?.date).format(defaultDateTimeFormat), defaultDateTimeFormat, dtf)
                    : moment(currentDataItem?.date).format(dtf);
                const d4 = fullDiffDays >= 1
                    ? extractTimeOnly(nextPeriodEndTime, defaultDateTimeFormat, dtf)
                    : moment(nextPeriodEndTime).format(dtf);
                dateLeft = d3 + ' — ' + d4;
                
                const d3r = fullDiffDays >= 1
                    ? extractTimeOnly(moment(currentDataItem?.date_compare).format(defaultDateTimeFormat), defaultDateTimeFormat, dtf)
                    : moment(currentDataItem?.date_compare).format(dtf);
                const d4r = fullDiffDays >= 1
                    ? extractTimeOnly(nextPeriodEndTimeCompare, defaultDateTimeFormat, dtf)
                    : moment(nextPeriodEndTimeCompare).format(dtf);
                dateRight = d3r + ' — ' + d4r;

                widerDateFormat = fullDiffDays == 0;
                break;
        }

        return (
            <div className={classes.tooltipContainer}>
                <div className={classes.toolTipHeader}>
                    <div style={{marginRight: 4}}>{dateLeft}</div>
                    {isDrawCompareWith && !widerDateFormat && (<div style={{marginRight: 4}}>{t('screens:calls.vs')}</div>)}
                    {isDrawCompareWith && !widerDateFormat && (<div>{dateRight}</div>)}
                </div>
                {isDrawCompareWith && widerDateFormat && (
                    <div className={classes.toolTipHeader}>
                        <div style={{marginRight: 4}}>{t('screens:calls.vs')}</div>
                    </div>
                )}
                {isDrawCompareWith && widerDateFormat && (
                    <div className={classes.toolTipHeader}>
                        <div>{dateRight}</div>
                    </div>
                )}
                <div className={classes.tooltipDetailsText}>
                    <div>{t('enums:callRecordType.8')}: </div>
                    <div className={classes.tooltipValueContainer}>
                        {getDurationFromSec(incomingValue).string}
                        {isDrawCompareWith && (<>
                            <div>{t('screens:calls.vs')}</div>
                            <div>{getDurationFromSec(incomingCompareValue).string}</div>
                        </>)}
                    </div>
                </div>
                <div className={classes.tooltipDetailsText}>
                    <div>{t('enums:callRecordType.4')}: </div>
                    <div className={classes.tooltipValueContainer}>
                        {getDurationFromSec(outgoingValue).string}
                        {isDrawCompareWith && (<>
                            <div>{t('screens:calls.vs')}</div>
                            <div style={{marginRight: 4}}>{getDurationFromSec(outgoingCompareValue).string}</div>
                        </>)}
                    </div>
                </div>
            </div>
        );
    }

export default CallStatisticsTooltip;